import { Action, createAction, createFeatureSelector, createReducer, createSelector, on, props } from '@ngrx/store';
import { Application, ApplicationGroupTile, ApplicationTile, ApplicationActiveState } from '../models/index';
import { EntityState } from '../../common/models/index';
import { fromJS } from 'immutable';

export interface GaApplicationsState {
    applications: ApplicationTile[];
    activeIdx: number;
    groups: ApplicationGroupTile[];
    state: EntityState;
    activeState: ApplicationActiveState;
    toggling: boolean;
    error: any;
}

export const applicationsInitialState: GaApplicationsState = {
    applications: [],
    activeIdx: -1,
    groups: [],
    state: EntityState.Empty,
    activeState: ApplicationActiveState.UnActive,
    toggling: false,
    error: null,
};

// ACTIONS

export enum ApplicationsActionType {
    ApplicationsInit = '[Applications] Init',
    ApplicationsAbort = '[Applications] Abort',
    ApplicationsSuccess = '[Applications] Success',
    ApplicationsFailure = '[Applications] Failure',
    ApplicationsCreate = '[Applications] Create',
    ApplicationsCreateSuccess = '[Applications] CreateSuccess',
    ApplicationsCreateFailure = '[Applications] CreateFailure',
    ApplicationsUpdate = '[Applications] Update',
    ApplicationsUpdateSuccess = '[Applications] UpdateSuccess',
    ApplicationsUpdateFailure = '[Applications] UpdateFailure',
    ApplicationsDelete = '[Applications] Delete',
    ApplicationsDeleteSuccess = '[Applications] DeleteSuccess',
    ApplicationsDeleteFailure = '[Applications] DeleteFailure',
    ApplicationsFindActive = '[Applications] FindActive',
    ApplicationsSetActive = '[Applications] SetActive'
}

export const applicationsInitAction = createAction(ApplicationsActionType.ApplicationsInit);
export const applicationsAbortAction = createAction(ApplicationsActionType.ApplicationsAbort);
export const applicationsSuccessAction = createAction(ApplicationsActionType.ApplicationsSuccess, props<{
    applications: ApplicationTile[],
    groups: ApplicationGroupTile[]
}>());
export const applicationsFailureAction = createAction(ApplicationsActionType.ApplicationsFailure, props<{ error: any }>());

export const applicationsCreateAction = createAction(ApplicationsActionType.ApplicationsCreate, props<{ application: Application }>());
export const applicationsCreateSuccessAction = createAction(ApplicationsActionType.ApplicationsCreateSuccess,
    props<{ application: Application }>());
export const applicationsCreateFailureAction = createAction(ApplicationsActionType.ApplicationsCreateFailure, props<{ error: any }>());

export const applicationsUpdateAction = createAction(ApplicationsActionType.ApplicationsUpdate,
    props<{ id: string, application: Application }>());
export const applicationsUpdateSuccessAction = createAction(ApplicationsActionType.ApplicationsUpdateSuccess,
    props<{ id: string, application: Application }>());
export const applicationsUpdateFailureAction = createAction(ApplicationsActionType.ApplicationsUpdateFailure, props<{ error: any }>());

export const applicationsDeleteAction = createAction(ApplicationsActionType.ApplicationsDelete, props<{ id: string }>());
export const applicationsDeleteSuccessAction = createAction(ApplicationsActionType.ApplicationsDeleteSuccess, props<{ id: string }>());
export const applicationsDeleteFailureAction = createAction(ApplicationsActionType.ApplicationsDeleteFailure, props<{ error: any }>());

export const applicationsFindActiveAction = createAction(ApplicationsActionType.ApplicationsFindActive, props<{
    applications: ApplicationTile[],
    force?: boolean
}>());
export const applicationsSetActiveAction = createAction(ApplicationsActionType.ApplicationsSetActive, props<{ id: string }>());

// SELECTORS

export const getApplicationsStoreState = createFeatureSelector<GaApplicationsState>('applications');

export const getApplicationsData = createSelector(getApplicationsStoreState, (state: GaApplicationsState) => state.applications);
export const getApplicationsActiveIdx = createSelector(getApplicationsStoreState, (state: GaApplicationsState) => state.activeIdx);
export const getApplicationsActiveApplication = createSelector(getApplicationsStoreState, (state: GaApplicationsState) => {
    return state.activeIdx >= 0 && state.applications && state.activeIdx < state.applications.length
        ? state.applications[state.activeIdx]
        : null;
});
export const getApplicationsGroups = createSelector(getApplicationsStoreState, (state: GaApplicationsState) => state.groups);
export const getApplicationsState = createSelector(getApplicationsStoreState, (state: GaApplicationsState) => state.state);
export const getApplicationsToggling = createSelector(getApplicationsStoreState, (state: GaApplicationsState) => state.toggling);
export const getApplicationsError = createSelector(getApplicationsStoreState, (state: GaApplicationsState) => state.error);
export const getApplicationsActiveState = createSelector(getApplicationsStoreState, (state: GaApplicationsState) => state.activeState);

// REDUCER

export const applicationsReducerFn = createReducer<GaApplicationsState>(
    applicationsInitialState,
    on(applicationsInitAction, (state) => {
        let map = fromJS(state);
        map = map.set('activeState', ApplicationActiveState.Progress);
        map = map.set('error', null);
        return map.toJS() as GaApplicationsState;
    }),
    on(applicationsAbortAction, (state) => {
        let map = fromJS(state);
        map = map.set('state', state.error
            ? EntityState.Error
            : state.applications && state.applications.length
                ? EntityState.Data
                : EntityState.Empty);

        switch (state.state) {
            case EntityState.Data:
                map = map.set('activeState', state.activeIdx !== -1 ? ApplicationActiveState.Active : ApplicationActiveState.NotFound);
                break;
            case EntityState.Empty:
                map = map.set('activeState', ApplicationActiveState.NotFound);
                break;
            case EntityState.Error:
                map = map.set('activeState', ApplicationActiveState.Error);
                break;
        }

        return map.toJS() as GaApplicationsState;
    }),
    on(applicationsSuccessAction, (state, { applications, groups }) => {
        let map = fromJS(state);
        map = map.set('error', null);
        map = map.set('applications', applications);
        map = map.set('groups', groups);
        const st = applications?.length > 0 ? EntityState.Data : EntityState.Empty;
        map = map.set('state', st);
        if (st === EntityState.Empty) {
            map = map.set('activeState', ApplicationActiveState.NotFound);
        }
        return map.toJS() as GaApplicationsState;
    }),
    on(applicationsFailureAction, (state, { error }) => {
        let map = fromJS(state);
        map = map.set('state', EntityState.Error);
        map = map.set('error', error);
        return map.toJS() as GaApplicationsState;
    }),
    on(applicationsCreateSuccessAction, (state, { application }) => {
        let map = fromJS(state);
        const apps = map.get('applications').toJS();
        apps.push(application);
        map = map.set('applications', apps);
        map = map.set('state', EntityState.Data);
        return map.toJS() as GaApplicationsState;
    }),
    on(applicationsCreateFailureAction, (state, { error }) => {
        let map = fromJS(state);
        map = map.set('error', error);
        return map.toJS() as GaApplicationsState;
    }),
    on(applicationsUpdateSuccessAction, (state, { id, application }) => {
        let map = fromJS(state);
        const apps: Application[] = map.get('applications').toJS();
        const idx = apps.findIndex(app => app.id === id);
        if (idx >= 0) { apps.splice(idx, 1); }
        apps.push(application);
        map = map.set('applications', apps);
        map = map.set('state', EntityState.Data);
        return map.toJS() as GaApplicationsState;
    }),
    on(applicationsUpdateFailureAction, (state, { error }) => {
        let map = fromJS(state);
        map = map.set('error', error);
        return map.toJS() as GaApplicationsState;
    }),
    on(applicationsDeleteSuccessAction, (state, { id }) => {
        let map = fromJS(state);
        const apps: Application[] = map.get('applications').toJS();
        const idx = apps.findIndex(app => app.id === id);
        if (idx >= 0) { apps.splice(idx, 1); }
        map = map.set('applications', apps);
        map = map.set('state', EntityState.Data);
        return map.toJS() as GaApplicationsState;
    }),
    on(applicationsDeleteFailureAction, (state, { error }) => {
        let map = fromJS(state);
        map = map.set('error', error);
        return map.toJS() as GaApplicationsState;
    }),
    on(applicationsSetActiveAction, (state, { id }) => {
        let map = fromJS(state);
        const idx: number = id ? state.applications.findIndex(app => app.id === id) : -1;
        map = map.set('activeIdx', idx);
        map = map.set('activeState', idx !== -1 ? ApplicationActiveState.Active : ApplicationActiveState.NotFound);
        return map.toJS() as GaApplicationsState;
    }),
);

export function applicationsReducer(state: GaApplicationsState | undefined, action: Action): GaApplicationsState {
    return applicationsReducerFn(state, action);
}
